import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ExternalLink, MessagesSquare } from 'lucide-react';
import { type Publication } from '../../entities/publication';
import { publicationsApi } from '../../shared/api/publications.api';
import { toast } from 'sonner';
import { commentsLink } from './lib/journal';
import { ROUTES } from '../../shared/config/routes';
import { Skeleton } from '../../components/ui/skeleton';

export function PublicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const [isBatch, setIsBatch] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (id) {
      loadPublicationAndRefreshInsights();
    }
  }, [id]);

  const loadPublicationAndRefreshInsights = async () => {
    try {
      setLoading(true);
      const result = await publicationsApi.getByBatchOrId(id!);
      
      setPublications(result.publications);
      setIsBatch(result.isBatch);

      if (result.publications.length === 0) {
        toast.error('Публикация не найдена');
        navigate(ROUTES.PUBLICATIONS);
        return;
      }

      // Автоматически обновляем статистику после загрузки
      setRefreshing(true);
      const updatedPublications = await publicationsApi.refreshInsights(id!);
      setPublications(updatedPublications);
      setRefreshing(false);
    } catch (error) {
      console.error('Error loading publication:', error);
      toast.error('Ошибка загрузки публикации');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadPublication = async () => {
    try {
      setLoading(true);
      const result = await publicationsApi.getByBatchOrId(id!);
      
      setPublications(result.publications);
      setIsBatch(result.isBatch);

      if (result.publications.length === 0) {
        toast.error('Публикация не найдена');
        navigate(ROUTES.PUBLICATIONS);
      }
    } catch (error) {
      console.error('Error loading publication:', error);
      toast.error('Ошибка загрузки публикации');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshInsights = async () => {
    setRefreshing(true);
    try {
      const updatedPublications = await publicationsApi.refreshInsights(id);
      setPublications(updatedPublications);
      toast.success('Статистика обновлена');
    } catch (error) {
      console.error('Error refreshing insights:', error);
      toast.error('Ошибка обновления статистики');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (publications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground mb-4">Публикация не найдена</p>
        <Button onClick={() => navigate(ROUTES.PUBLICATIONS)}>
          Вернуться к публикациям
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>
              {isBatch ? `Детали публикации (${publications.length} аккаунтов)` : 'Детали публикации'}
            </CardTitle>
            <CardDescription>
              {refreshing ? 'Обновление статистики...' : 'Статистика по публикации в аккаунтах'}
            </CardDescription>
          </div>
          {(() => {
            // До пяти ссылок за раз — больше разбор не принимает.
            const comments = commentsLink(publications.map((p) => p.permalink));
            return comments && (
              <Button variant="outline" size="sm" onClick={() => navigate(comments)}>
                <MessagesSquare className="size-4" />
                Разобрать комментарии
              </Button>
            );
          })()}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Аккаунт</TableHead>
                <TableHead className="text-right">ER</TableHead>
                <TableHead className="text-right">Просмотры</TableHead>
                <TableHead className="text-right">Лайки</TableHead>
                <TableHead className="text-right">Комментарии</TableHead>
                <TableHead className="text-right">Репосты</TableHead>
                <TableHead className="text-right">Сохранения</TableHead>
                <TableHead className="text-right">Охват</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {publications.map((pub) => {
                const calculateER = () => {
                  // Используем views в первую очередь (для reels), если нет - используем reach (для постов)
                  const denominator = pub.insights?.views || pub.insights?.reach || 0;
                  if (denominator === 0) return null;
                  
                  const engagement = 
                    (pub.insights.likes || 0) + 
                    (pub.insights.comments || 0) + 
                    (pub.insights.shares || 0) + 
                    (pub.insights.saves || 0);
                  
                  const er = (engagement / denominator) * 100;
                  return er.toFixed(2) + '%';
                };

                return (
                  <TableRow 
                    key={pub.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => window.open(pub.permalink, '_blank')}
                  >
                    <TableCell>
                      <div className="font-medium">{pub.accountName}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      {calculateER() || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {pub.insights?.views?.toLocaleString() || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {pub.insights?.likes?.toLocaleString() || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {pub.insights?.comments?.toLocaleString() || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {pub.insights?.shares?.toLocaleString() || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {pub.insights?.saves?.toLocaleString() || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {pub.insights?.reach?.toLocaleString() || '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}